// interfaces/types
import type {
    ContactEmail,
    ContactPhone,
    DXGCreatedBy,
    DXGCoxAutoReferences,
    ITradeIn,
    DXGName
} from '@makemydeal/dr-platform-types/';
import type { IDxgVehicle, SalesViewMode } from '@makemydeal/dr-shared-types';
import type { BaggageEntry } from '@makemydeal/dr-platform-shared';

//constants/enums
import type { EmployeeRole, RelationShip, Persona, DealType, IncentiveType } from './enums';

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

// Request type for fetching deals with optional filters
export type FetchDealsRequest = {
    dealerKeyName?: string;
    dealerKeyValue?: number;
    email?: string;
    commonOrgId?: string;
    phone?: string;
    pageCursor?: string;
    pageSize?: number;
};

// Request type for deal search endpoint
export type DealSearchRequest = {
    lastName?: string;
    emailAddress?: string;
    phoneNumber?: string;
    dealId: string;
};

// Filter reasons for excluded deals
export enum DealFilterReason {
    MISSING_COX_AUTO_REFERENCES = 'missing coxAutoReferences',
    MISSING_CREDIT_APP_ID = 'missing creditApplicationId',
    CASH_DEAL = 'cash deal',
    DEAL_FINALIZED = 'deal finalized',
    BUSINESS_DEAL = 'business deal'
}

// Filtered deal metadata
export type FilteredDeal = {
    dealId: string;
    filterReason: DealFilterReason;
    missingFields?: string[];
    finalizedDates?: {
        contractSignedDate?: string;
        finalizedDate?: string;
    };
};

// Matched deal structure for search results
export type IDealSearchDeal = {
    coxAutoReferences: {
        dealRefId: string;
        dealRefIdPartner: {
            partnerCode: string;
            partnerDealerId: string;
        };
    };
    dealLifecycle?: {
        contractSignedDate?: string;
        finalizedDate?: string;
    };
    dealStructure: {
        participants?: {
            buyer?: Consumer;
            coBuyer?: Cobuyer;
        };
        vehicles?: {
            sellingVehicle?: VehicleDetails;
        };
        dealType: string;
        creditApplication?: {
            creditApplicationId?: string;
        };
        versionCreatedOn?: string;
    };
    participants?: Participant[];
    dealId: string;
};

// Response type for deal search endpoint
export type DealSearchResponse = {
    matchedDeals: IDealSearchDeal[];
    filteredDeals: FilteredDeal[];
};

// Response type for fetching deals, including pagination links
export type FetchDealsResponse = {
    deals: [];
    nextPageLink?: string;
    previousPageLink?: string;
};

export type FetchDealChangeLogInput = {
    dealId: string;
    skipPagination?: boolean;
    pageSize?: number;
    pageCursor?: string;
};

// ============================================================================
// CORE DOMAIN ENTITIES
// ============================================================================

export type ApplicationInfo = Omit<DXGCreatedBy, 'dealerUser'> & {
    dealerUser?: string;
};

// References specific to Cox Automotive
export type CoxAutoReferences = Omit<DXGCoxAutoReferences, 'commonOrgId'> & {
    commonOrgId: string;
    dealRefIdPartner: {
        partnerCode: string;
        partnerDealerId: string;
    };
};

export type DealLifecycle = {
    createdOn: string;
    status: string;
    approvedDate?: string;
    contractSignedDate?: string;
    deliveredDate?: string;
    soldDate?: string;
};

export type KeyValuePair = {
    name: string;
    value: string;
};

export type DealerReference = KeyValuePair;

// ============================================================================
// PERSON AND CONTACT INFORMATION TYPES
// ============================================================================

// Base person information
export type PersonName = Omit<DXGName, 'firstName'> & {
    firstName?: string;
    birthMonth?: string;
    birthYear?: string;
};

// Consolidated Address type (replaces both CustomerAddress and Address)
export type Address = {
    country?: string;
    postalCode?: string;
    stateProvince?: string;
    countyDistrict?: string;
    city?: string;
    addressType?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
};

// Contact information types - using DXG types that only add optional contactType field
export type ContactInfo = ContactEmail;

export type PhoneContactInfo = ContactPhone;

// Business signer information for business deals
export type BusinessSigner = {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    salutation?: string;
    suffix?: string;
};

// Customer extends PersonName with additional required fields
export type CustomerInfo = PersonName & {
    consumerId: string;
    email: string;
    emailAlternative?: string;
    phone: string;
    cellPhone?: string;
    homePhone?: string;
    workPhone?: string;
    postalCode?: string;
    dealershipId: string;
    address?: Address;
    legalName?: string;
    // Business customer fields
    isBusinessCustomer?: boolean;
    businessEntityId?: string;
    businessSigner?: BusinessSigner;
    customerType?: 'Individual' | 'Business';
};

// Override postalCode to be required for CoBuyerInfo
export type CoBuyerInfo = Omit<CustomerInfo, 'postalCode'> & {
    postalCode: string;
    relationshipToBuyer: RelationShip;
};

export type SalesPersonInfo = {
    bridgeId: string;
    firstName: string;
    lastName: string;
    userName: string;
    fullName?: string;
};

export enum CustomerType {
    INDIVIDUAL = 'Individual',
    BUSINESS = 'Business'
}

export type Consumer = PersonName & {
    commonConsumerId: string;
    emailAddress?: string;
    estimatedCreditScore?: number;
    address?: Address;
    phoneNumber?: string;
    cellPhone?: string;
    homePhone?: string;
    workPhone?: string;
    legalName?: string;
    // Business customer fields
    businessEntityId?: string;
    businessSigner?: BusinessSigner;
    customerType?: CustomerType;
};

// Used by search & attach feature to filter out business deals
export type BusinessConsumer = Omit<PersonName, 'lastName'> & {
    commonConsumerId: string;
    customerType?: string;
    emailAddress: string;
    estimatedCreditScore?: number;
    address?: Address;
    phoneNumber?: string;
    legalName: string;
    lastName?: never; // Explicitly exclude lastName for business consumers
    businessEntityId?: string;
    businessSigner?: BusinessSigner;
};

export type Cobuyer = Consumer & {
    relationshipToBuyer?: RelationShip;
};

export type Participant = {
    commonConsumerId?: string;
    customerType?: string;
    emailContactInfo?: ContactInfo;
    phoneContactInfo?: PhoneContactInfo;
    estimatedCreditScore?: number;
    name?: PersonName;
    primaryAddress?: Address;
    additionalContacts?: ContactInfo[];
    // Business customer fields
    legalName?: string;
    businessEntityId?: string;
    businessSigner?: BusinessSigner;
};

type DealXGParticipant = {
    commonConsumerId: string;
    customerType: string;
    emailContactInfo: ContactInfo;
    estimatedCreditScore: number;
    name: PersonName;
    primaryAddress: Address;
    // Business customer fields
    legalName?: string;
    businessEntityId?: string;
    businessSigner?: BusinessSigner;
};

// ============================================================================
// FINANCIAL AND PRICING TYPES
// ============================================================================

// Financial Details Types (exported for reusability)
export type PaymentDetails = {
    // `payment` and `amountFinanced` are optional because a dealer-review invalidation omits them; `term`
    // is what a person chose and is always kept. See `financialDetailsInvalidator` in shared-bff-utils.
    amountFinanced?: number;
    payment?: number;
    term: number;
    paymentFrequency?: string;
    annualMiles?: number;
    specialPaymentFormula?: string;
    paymentSchedules?: { scheduledPayment: { term: number; amount: number } }[];
    comparableStandardPayment?: number;
    totalBalloonPaymentAmount?: number;
    balloonTermType?: string;
    totalOfPaymentsAndConsideration?: number;
};

// Price details in financial information
export type PriceDetails = {
    currentOfferPrice?: number;
    retailPrice?: number;
    sellingPrice?: number;
    isOnePrice?: boolean;
    discountOfMsrp?: number;
    priceWithAdds?: number;
    priorLeaseBalance?: { amount?: number };
    itemizedDiscounts?: { discountName?: string; discountAmount: number }[];
};

// Sell rate details
export type SellRate = {
    apr: number;
    moneyFactor?: number;
};

// Rate details in financial information
export type RateDetails = {
    sellRate: SellRate;
};

/**
 * State of the payment saved on a deal version — acceleron reads it on init to decide whether to trust the
 * saved numbers. `PendingDealerReview` means the payment didn't fail; it needs a manager to re-desk.
 */
export enum PaymentCalculationStatus {
    Current = 'Current',
    Failed = 'Failed',
    PendingDealerReview = 'PendingDealerReview'
}

export type PaymentCalculationState = {
    paymentStatus?: PaymentCalculationStatus;
};

/** Calculation metadata on the deal. Only the nodes read or written here are typed. */
export type DealCalculationDetails = {
    payment?: {
        calculationState?: PaymentCalculationState;
        [key: string]: unknown;
    };
    [key: string]: unknown;
};

/**
 * Adjusted cap cost and its calculated siblings (lease). Only the nodes a dealer-review invalidation
 * clears are named; anything else the deal carries survives via the index signature.
 */
export type CapCostDetails = {
    adjustedCapCost?: number;
    grossCapCost?: number;
    totalCapReduction?: number;
    capReductionCash?: number;
    capReductionRebate?: number;
    capReductionTradeIn?: number;
    addsToCapCostTotal?: number;
    [key: string]: unknown;
};

/**
 * Money due in advance. `netCashDown`/`minimumCashDown` are what a person chose and are kept; the rest
 * are calculated and cleared on a dealer review. Only the nodes SCI reads or clears are named.
 */
export type DueInAdvanceDetails = {
    netCashDown?: number;
    minimumCashDown?: number;
    dueAtSigning?: number;
    customerDueAtSigning?: number;
    cashDueFromCustomer?: number;
    cashAppliedToUpfrontItems?: number;
    dealerCashAppliedToUpfrontItems?: number;
    rebateAppliedToUpfrontItems?: number;
    tradeDownAppliedToUpfrontItems?: number;
    tradeDown?: number;
    rebate?: number;
    totalSecurityDeposit?: number;
    [key: string]: unknown;
};

/** Persisted fee rollup totals. Calculated, so cleared on a dealer review. */
export type FeeSummary = {
    totalFees?: number;
    totalCapitalizedFees?: number;
    totalUpfrontFees?: number;
    [key: string]: unknown;
};

/**
 * A fee line. `isManualFee` is the signal SCI reads on a dealer review: `true` = a manager edited this
 * fee, so keep the line; otherwise the system calculated it and the line is removed.
 */
export type FeeLine = {
    isManualFee?: boolean;
    feeName?: string;
    feeAmount?: number;
    feeTypeId?: string | number;
    feeCategory?: string;
    isFeeCapitalized?: boolean;
    isFeeIncludedInCalculation?: boolean;
    [key: string]: unknown;
};

/** Fee lines and their (derived, not persisted) summary. */
export type FeeDetails = {
    feeSummary?: FeeSummary;
    fees?: FeeLine[];
    [key: string]: unknown;
};

/** Persisted tax rollup totals. Calculated, so cleared on a dealer review. */
export type TaxSummary = {
    totalTax?: number;
    totalCapitalizedTax?: number;
    totalUpfrontTax?: number;
    isTaxCapitalized?: boolean;
    [key: string]: unknown;
};

/** One jurisdiction's slice of a tax line, keyed under `taxBreakdown` (State / County / City / Other). */
export type TaxBreakdownEntry = {
    isManualRate?: boolean;
    isManualTaxAmount?: boolean;
    rate?: number;
    taxTotal?: number;
    jurisdictionCode?: string;
    jurisdictionDescription?: string;
    [key: string]: unknown;
};

/**
 * A tax line. `isManualTax` is a line-level Total Tax Override; the per-jurisdiction manager edits live
 * inside `taxBreakdown` as `isManualRate` / `isManualTaxAmount`. The keep/clear mapping across these
 * signals is unconfirmed by Core Deal — see OQ-023 and `invalidateTaxLines`.
 */
export type TaxLine = {
    isManualTax?: boolean;
    taxName?: string;
    taxRate?: number;
    taxAmount?: number;
    taxBasisAmount?: number;
    taxBreakdown?: Record<string, TaxBreakdownEntry>;
    [key: string]: unknown;
};

/** Tax lines and their (derived, not persisted) summary. */
export type TaxDetails = {
    taxSummary?: TaxSummary;
    taxes?: TaxLine[];
    [key: string]: unknown;
};

export type AccessoryDetails = {
    accessories?: { accessoryName?: string; accessoryPrice: number }[];
};

export type ProductDetails = {
    products?: {
        productName?: string;
        productPrice: number;
        productCategoryCode?: string;
        productCoverageLength?: number;
        productCoverageMiles?: number;
        productDeductible?: number;
    }[];
    productSummary?: { totalProductAmount?: number };
};

export type IncentiveDetails = {
    incentives?: {
        programName?: string;
        cash: number;
        incentiveType?: string;
    }[];
    incentiveSummary?: { appliedIncentiveAmount?: number };
};

export type ResidualDetails = {
    netResidual?: number;
};

export type ProgramDetails = {
    lenderLongName?: string;
};

export type CashFinancialDetails = {
    calculationDetails?: DealCalculationDetails;
    cashDownPayment?: number;
    paymentDetails?: PaymentDetails;
    priceDetails?: PriceDetails;
    rateDetails?: RateDetails;
    capCostDetails?: CapCostDetails;
    dueInAdvanceDetails?: DueInAdvanceDetails;
    feeDetails?: FeeDetails;
    taxDetails?: TaxDetails;
    totalBalanceDue?: number;
    dealDeposit?: number;
    totalOfPaymentsAndConsideration?: number;
    accessoryDetails?: AccessoryDetails;
    productDetails?: ProductDetails;
    incentiveDetails?: IncentiveDetails;
    residualDetails?: ResidualDetails;
    programDetails?: ProgramDetails;
};

// Pricing and cost details of a vehicle
export type PriceCostDetails = {
    invoiceAmount?: number;
    originalMsrp?: number;
    retailPrice?: number;
    retailPriceName?: string;
    salesCost?: number;
};

export type CreditApplication = {
    creditApplicationId?: string;
    creditDecision?: unknown;
    submitter?: string;
};

// ============================================================================
// VEHICLE TYPES
// ============================================================================

export type VehicleDetails = IDxgVehicle & {
    vehicleDetailPage?: string;
    classification?: string;
};

// Vehicle-related types

export type VehicleCode = {
    codeType: string;
    chromeStyleId: number;
} & Record<string, string | number | undefined>;

export type Descriptions = {
    description?: string;
};

// Powertrain details of a vehicle
export type Powertrain = {
    engineDetails: Descriptions;
    transmissionDetails: Descriptions;
};

export type CreateDealVehicleInfo = {
    vin: string;
    year: string;
    make: string;
    model: string;
    trim: string;
    bodyStyle: string;
    exteriorColor: string;
    interiorColor: string;
    odometer: number;
    engine: string;
    transmission: string;
    chromeId: number;
    stockNumber: string;
    link: string;
    images: string;
    msrp?: number;
    startingPrice: number;
    finalPrice: number;
    uuid: string;
    inventoryType: string;
    certified: boolean;
};

export type ITradeInVehicles = {
    //TODO: to be revisited as part of arch changes
    tradeIns: (ITradeIn | DxgTradeInVehicle)[];
    tradeInSummary: any;
};

// DealXG-shaped trade-in record used when aggregating the trade-in summary.
export type DxgTradeInVehicle = {
    tradeAllowance?: number;
    tradePayoffDetails?: { tradePayoffAmount?: number };
    tradeValuation?: { actualCashValue?: number };
    tradeInTaxReduction?: number;
};

// Vehicles involved in a deal
export type Vehicles = {
    sellingVehicle?: VehicleDetails;
    tradeInVehicles?: ITradeInVehicles;
};

// ============================================================================
// DEAL STRUCTURE AND PARTICIPANT TYPES
// ============================================================================

// Participants involved in a deal
export type DealParticipants = {
    buyer: Consumer | BusinessConsumer;
    coBuyer?: Consumer;
};

export type DealStructureExtended = DealStructure & {
    versionCreatedBy?: ApplicationInfo;
};

// Deal employee type
export type DealEmployee = {
    bridgeId: string;
    employeeRole: EmployeeRole;
    name: PersonName;
    referenceId: string;
    employeeDmsId?: string;
    employeeLicenseNumber?: string;
    emailContactInfo?: ContactInfo;
    phoneContactInfo?: PhoneContactInfo;
    additionalContacts?: (ContactInfo | PhoneContactInfo)[];
};

export type DealStructure = {
    acceptedByBuyer?: boolean;
    currencyCode?: string;
    dealType: string;
    financialDetails: CashFinancialDetails;
    isDealInitialOpportunity?: boolean;
    participants?: DealParticipants;
    vehicles?: Vehicles;
    versionCreatedOn?: string;
    versionCreatedBy?: ApplicationInfo;
    version?: string;
    creditApplication?: CreditApplication;
    versionComment?: string;
    visibility?: DealVisibility;
};

export type DealVisibility = {
    approvedByManager: boolean;
    needsManagerApproval: boolean;
    isDraftDeal?: boolean;
};

export type DealXgCreateRequest = {
    changedBy: ApplicationInfo;
    coxAutoReferences: CoxAutoReferences;
    dealers: DealerReference[];
    dealLifecycle: DealLifecycle;
    language?: string;
    participants?: Participant[];
    references?: DealerReference[];
    saleType?: string;
    dealStructure: DealStructureExtended;
    employees: DealEmployee[];
    baggage?: BaggageEntry[];
};

export type DxgDealer = {
    name: string;
    value: string;
};

export type DealXG = {
    changedBy: ApplicationInfo;
    coxAutoReferences: CoxAutoReferences;
    dealers: KeyValuePair[];
    dealLifecycle: DealLifecycle;
    language: string;
    participants: DealXGParticipant[];
    references: KeyValuePair[];
    saleType: string;
    'X-CoxAuto-Correlation-Id': string;
    meta: Meta;
    dealId: string;
    dealStructure: DealStructure;
    dealProgressMilestones: DealProgressMilestones;
    dealComment?: string;
    employees?: DealEmployee[];
    baggage?: BaggageEntry[];
    documents?: Documents[];
    artifacts?: Artifacts[];
};

// Milestones tracking the progress of a deal
type DealProgressMilestones = {
    [key: string]: DealProgressMilestone;
};

// Metadata associated with a deal
type Meta = {
    current: boolean;
    clientId: string;
    apiVersion: string;
    requestApiVersion: string;
    lastModifiedDate: string;
    clientName: string;
};

// Milestone details for deal progress
type DealProgressMilestone = {
    state: string;
    persona: string;
    applicationName: string;
    updatedOn: string;
};

// ============================================================================
// PAYLOAD TYPES FOR API OPERATIONS
// ============================================================================

export type DealerDetails = {
    mmdId: string;
};

export type CreateDealPayload = {
    customerInfo: CustomerInfo;
    vehicleInfo?: IDxgVehicle;
    salespersonInfo?: SalesPersonInfo;
    dealer: DealerDetails;
    coBuyer?: CoBuyerInfo;
};

export type UpdateDealPayload = CreateDealPayload & {
    dealXgId: string;
    shouldUpdateConsumerProfile?: boolean;
    /**
     * The dealer's edition, carried verbatim so the BFF can resolve capabilities for it. Replaces
     * the old `isShowroomMode` boolean, which could not represent Sales Intake as its own mode.
     * Typed `string` (like `DealerSettings.salesViewMode`) and narrowed at the point of use.
     */
    salesViewMode?: SalesViewMode;
    dealSummary?: DealSummary;
    visibility?: DealVisibility;
    acceptedByBuyer?: boolean;
    changeType?: DealVersionChangeType;
    dealXgVersion?: string;
};

export type DealSummary = {
    dealTypes?: DealType[];
    incentives?: IncentiveType[];
    notes?: string;
};

/**
 * Identifies which SCI page/intent produced a deal version. Drives the version comment, and is the
 * declared intent behind a `deal360.dealerReview` notification.
 */
export enum DealVersionChangeType {
    REQUEST_PROPOSAL = 'requestProposal',
    VEHICLE = 'vehicle',
    CUSTOMER = 'customer',
    TRADE = 'trade',
    DEAL_SETUP = 'deal_setup'
}

// ============================================================================
// UTILITY AND METADATA TYPES
// ============================================================================

export type ApplicationMetaData = {
    applicationName: string;
    persona: Persona;
};

export type Documents = {
    comment: string;
    documentStatus: string;
    documentType?: string;
    href?: string;
    submitter?: string;
    updateTimeStamp?: string;
};

export type Artifacts = {
    artifactType: string;
    comment: string;
    href?: string;
    submitter?: string;
    updateTimeStamp?: string;
};
